import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Camera, Upload, X, Loader2, ScanLine, 
  FileText, ArrowRight, ShieldCheck 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useLanguage } from '../context/LanguageContext';
import { apiService } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Button } from './ui/button';

const QuickScanModal = ({ isOpen, onClose }) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [isScanning, setIsScanning] = useState(false);
  const [scanStep, setScanStep] = useState(0);

  const scanSteps = [
    t('scan_step_1') || "Initializing Neural Modules...",
    t('scan_step_2') || "OCR Layer Extraction...",
    t('scan_step_3') || "Parsing Dates & Currency...",
    t('scan_step_4') || "Categorizing Document...",
    t('scan_step_5') || "Encrypting for Vault...",
    t('scan_step_6') || "Finalizing Results..."
  ];

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // 1. Tarama Efekti Başlat
    setIsScanning(true);
    
    // 2. Adım Adım Simülasyon (Görsel Zenginlik İçin)
    for (let i = 0; i < scanSteps.length; i++) {
        setScanStep(i);
        await new Promise(r => setTimeout(r, 600)); // Her adımda biraz bekle
    }

    // 3. Dosyayı Gönder
    try {
        const formData = new FormData();
        formData.append('file', file);
        
        // Backend'e gönder
        const response = await apiService.documents.analyze(formData);
        
        toast({
            title: t('scan_success') || "Scan Complete",
            description: t('scan_success_desc') || "Document processed successfully.",
            className: "bg-emerald-500 text-white border-0"
        });

        // 4. Analiz Sonucuna Yönlendir
        onClose();
        navigate('/archive'); // Veya direkt analiz detayına: navigate('/analysis-result', { state: { document: response.data } });

    } catch (error) {
        console.error("Scan Error:", error);
        toast({
            title: t('scan_error') || "Scan Failed",
            description: "Could not process document. Please try again.",
            variant: "destructive"
        });
    } finally {
        setIsScanning(false);
        setScanStep(0);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/90 backdrop-blur-md"
          />

          {/* Modal Content */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="relative w-full max-w-lg bg-[#0B1120] border border-white/10 rounded-[2.5rem] shadow-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-8 border-b border-white/10 bg-white/[0.02]">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-orange-500 rounded-xl text-white shadow-lg shadow-orange-600/20 animate-pulse">
                        <ScanLine size={24} />
                    </div>
                    <div>
                        <h2 className="text-lg font-black text-white uppercase tracking-tight">
                            SMART <span className="text-orange-500">SCAN</span>
                        </h2>
                        <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                            AI-POWERED OCR V3.0
                        </p>
                    </div>
                </div>
                {!isScanning && (
                    <button onClick={onClose} className="p-3 bg-white/5 hover:bg-white/10 rounded-2xl transition-colors text-slate-400 hover:text-white">
                        <X size={20} />
                    </button>
                )}
            </div>

            {/* Body */}
            <div className="p-8">
                {isScanning ? (
                    <div className="flex flex-col items-center justify-center py-8 text-center space-y-6">
                        <div className="relative w-32 h-32">
                            <div className="absolute inset-0 border-4 border-orange-500/20 rounded-full"></div>
                            <div className="absolute inset-0 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-2xl font-black text-white">{Math.round(((scanStep + 1) / scanSteps.length) * 100)}%</span>
                            </div>
                        </div>
                        
                        <div className="space-y-2 h-12">
                            <motion.p 
                                key={scanStep}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                className="text-sm font-bold text-orange-400 uppercase tracking-widest"
                            >
                                {scanSteps[scanStep]}
                            </motion.p>
                        </div>

                        <div className="flex items-center gap-2 text-xs text-slate-500 bg-white/5 px-4 py-2 rounded-full">
                            <ShieldCheck size={12} className="text-emerald-500" />
                            <span>AES-256 Encryption Active</span>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-4">
                        <input 
                            type="file" 
                            ref={fileInputRef} 
                            onChange={handleFileUpload} 
                            className="hidden" 
                            accept="image/*,application/pdf"
                        />
                        
                        <button 
                            onClick={() => fileInputRef.current?.click()}
                            className="group relative h-32 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-[2rem] flex flex-col items-center justify-center gap-3 overflow-hidden shadow-lg transition-transform active:scale-95"
                        >
                            <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-500"></div>
                            <Upload size={32} className="text-white relative z-10" />
                            <span className="text-xs font-black text-white uppercase tracking-widest relative z-10">
                                {t('upload_from_device') || 'Upload File'}
                            </span>
                        </button>

                        <div className="grid grid-cols-2 gap-4">
                            <button className="h-24 bg-[#1e293b] hover:bg-[#253045] rounded-[1.5rem] border border-white/5 flex flex-col items-center justify-center gap-2 transition-colors group">
                                <Camera size={24} className="text-slate-400 group-hover:text-white transition-colors" />
                                <span className="text-[10px] font-bold text-slate-500 group-hover:text-slate-300 uppercase tracking-wider">
                                    {t('camera_capture') || 'Camera'}
                                </span>
                            </button>
                            <button className="h-24 bg-[#1e293b] hover:bg-[#253045] rounded-[1.5rem] border border-white/5 flex flex-col items-center justify-center gap-2 transition-colors group">
                                <FileText size={24} className="text-slate-400 group-hover:text-white transition-colors" />
                                <span className="text-[10px] font-bold text-slate-500 group-hover:text-slate-300 uppercase tracking-wider">
                                    Manual Entry
                                </span>
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Footer */}
            {!isScanning && (
                <div className="p-6 bg-white/[0.02] border-t border-white/10 text-center">
                    <p className="text-[10px] text-slate-500">
                        Supported formats: PDF, JPG, PNG (Max 10MB)
                    </p>
                </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default QuickScanModal;